import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useLang } from "@/i18n/LanguageProvider";
import {
  readConsent,
  writeConsent,
  OPEN_CONSENT_EVENT,
} from "@/lib/consent";

export function ConsentBanner() {
  const { t } = useTranslation("consent");
  const { href } = useLang();
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [ads, setAds] = useState(false);

  useEffect(() => {
    if (readConsent() == null) setOpen(true);
    const handler = () => {
      const c = readConsent();
      setAnalytics(!!c?.analytics);
      setAds(!!c?.ads);
      setShowSettings(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_CONSENT_EVENT, handler);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, handler);
  }, []);

  if (!open) return null;

  function save(state: { analytics: boolean; ads: boolean }) {
    writeConsent(state);
    setOpen(false);
    setShowSettings(false);
  }

  return (
    <div
      role="dialog"
      aria-label={t("title")}
      aria-modal="false"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-rule bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 shadow-lg"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 py-4 sm:py-5">
        <div className="flex flex-col gap-3 sm:gap-4">
          <div className="text-sm text-foreground">
            <span className="font-medium">{t("title")} </span>
            <span className="text-muted-foreground">{t("body")} </span>
            <Link
              to={href("/integritetspolicy")}
              className="underline underline-offset-4 hover:text-foreground text-muted-foreground"
            >
              {t("readMore")}
            </Link>
          </div>

          {showSettings && (
            <div className="grid sm:grid-cols-3 gap-3 border border-rule rounded-md p-3 bg-background/50">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-medium">{t("cat.necessary.title")}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{t("cat.necessary.desc")}</div>
                </div>
                <Switch checked disabled aria-label={t("cat.necessary.title")} />
              </div>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-medium">{t("cat.analytics.title")}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{t("cat.analytics.desc")}</div>
                </div>
                <Switch checked={analytics} onCheckedChange={setAnalytics} aria-label={t("cat.analytics.title")} />
              </div>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-medium">{t("cat.ads.title")}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{t("cat.ads.desc")}</div>
                </div>
                <Switch checked={ads} onCheckedChange={setAds} aria-label={t("cat.ads.title")} />
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 justify-end">
            {!showSettings && (
              <Button variant="outline" size="sm" onClick={() => setShowSettings(true)}>
                {t("settings")}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => save({ analytics: false, ads: false })}>
              {t("rejectAll")}
            </Button>
            {showSettings && (
              <Button variant="outline" size="sm" onClick={() => save({ analytics, ads })}>
                {t("savePrefs")}
              </Button>
            )}
            <Button size="sm" onClick={() => save({ analytics: true, ads: true })}>
              {t("acceptAll")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}